import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import apiConfig from "../utils/apiConfig";
import { IPagedResponse } from "../components/shared/Tables/PagedTable";
import { PageMode } from "../models/PagedTable";
import { IPart, ISingleCategoryPartResponse } from "../models/Part";

interface LookupBody {
  searchBody: Record<string, unknown>;
}

// Dev-time logging: flip to `false` once the lookup is happy. Runs a few
// diagnostic console.logs through the lookup so it's easy to see in the
// browser why a part isn't resolving from a message's `itemID`.
const DEBUG = true;
const dlog = (...args: unknown[]) => {
  if (DEBUG && typeof window !== "undefined") {
    // eslint-disable-next-line no-console
    console.log("[usePartByEbayItem]", ...args);
  }
};

/**
 * Resolves a part by its eBay item number (the numeric id on
 * `www.ebay.co.uk/itm/<id>`).
 *
 * Until the dedicated `/part/byebay/:id` endpoint lands (see
 * `docs/pending-endpoints.md`), we do a best-effort lookup against the
 * existing paged listings endpoint. Different vintages of listings in
 * the DB store the item number in different fields, so we try the
 * likely candidates in turn and accept the first hit.
 *
 * Resolution order:
 *   1. `searchBody: { ebayItemNumber }` across Listed → Stock
 *   2. `searchBody: { ebayTradingApiItemId }` across Listed → Stock
 *      (legacy Trading API listings)
 *   3. `?query=<itemNumber>` freeform search
 *   4. Client-side match on any of {ebayItemNumber,
 *      ebayTradingApiItemId, offers[].ebayId} to avoid false positives.
 *
 * Once we have an internal id, we follow up with `/part/:id/offers` —
 * the same endpoint `useSinglePart` uses — so consumers (e.g. the
 * messaging modal's StockHeader) see the full part payload instead of
 * the slim paged-row shape.
 */
export default function usePartByEbayItem(
  itemNumber: string | null | undefined,
  componentName = "usePartByEbayItem"
) {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const enabled = Boolean(itemNumber);
  const staleTime = 1000 * 60 * 5;

  const lookup = (
    label: string,
    pageMode: PageMode,
    body: Record<string, unknown>,
    urlQuery?: string
  ) =>
    post<LookupBody, IPagedResponse<IPart>>(
      `/part/listings/1?pageMode=${pageMode}&pageSize=5${
        urlQuery ? `&query=${encodeURIComponent(urlQuery)}` : ""
      }`,
      { searchBody: body }
    ).then((res) => {
      dlog(label, {
        itemNumber,
        rows: res?.data?.rows?.length ?? 0,
        total: res?.data?.total ?? 0,
        sample: res?.data?.rows?.[0]
          ? {
              id: res.data.rows[0].id,
              ebayItemNumber: res.data.rows[0].ebayItemNumber,
              ebayTradingApiItemId: res.data.rows[0].ebayTradingApiItemId,
            }
          : null,
      });
      return res;
    });

  const byNumberListed = useQuery({
    queryKey: ["partByEbayItem", "by-number-listed", itemNumber],
    queryFn: () =>
      lookup("byNumber/Listed", PageMode.Listed, {
        ebayItemNumber: itemNumber,
      }),
    enabled,
    staleTime,
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const hasBuyersFor = (q: ReturnType<typeof useQuery>): boolean =>
    !q.isLoading &&
    !q.isFetching &&
    !((q.data as IPagedResponse<IPart> | undefined)?.data?.rows?.length ?? 0);

  const byNumberStockEnabled =
    enabled && hasBuyersFor(byNumberListed as ReturnType<typeof useQuery>);
  const byNumberStock = useQuery({
    queryKey: ["partByEbayItem", "by-number-stock", itemNumber],
    queryFn: () =>
      lookup("byNumber/Stock", PageMode.Stock, {
        ebayItemNumber: itemNumber,
      }),
    enabled: byNumberStockEnabled,
    staleTime,
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const byTradingEnabled =
    byNumberStockEnabled &&
    hasBuyersFor(byNumberStock as ReturnType<typeof useQuery>);
  const byTrading = useQuery({
    queryKey: ["partByEbayItem", "by-trading", itemNumber],
    queryFn: () =>
      lookup("byTrading/Stock", PageMode.Stock, {
        ebayTradingApiItemId: itemNumber,
      }),
    enabled: byTradingEnabled,
    staleTime,
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const byQueryEnabled =
    byTradingEnabled && hasBuyersFor(byTrading as ReturnType<typeof useQuery>);
  const byQuery = useQuery({
    queryKey: ["partByEbayItem", "by-query", itemNumber],
    queryFn: () => lookup("byQuery/Stock", PageMode.Stock, {}, itemNumber ?? ""),
    enabled: byQueryEnabled,
    staleTime,
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  // Defensively client-side match so we never hand back an unrelated
  // part (can happen with the freeform `query` fallback, which searches
  // titles / descriptions as well as indexed ids).
  const pickExact = (rows: IPart[] | undefined): IPart | undefined => {
    if (!rows?.length || !itemNumber) return undefined;
    return (
      rows.find((r) => r.ebayItemNumber === itemNumber) ??
      rows.find((r) => r.ebayTradingApiItemId === itemNumber) ??
      rows.find((r) =>
        (r.offers ?? []).some((o) => o.ebayId === itemNumber)
      )
    );
  };

  const slimPart =
    pickExact(byNumberListed.data?.data?.rows) ??
    pickExact(byNumberStock.data?.data?.rows) ??
    pickExact(byTrading.data?.data?.rows) ??
    pickExact(byQuery.data?.data?.rows) ??
    // Last resort: trust the single-row response from the strict filters
    // (they're already exact on the backend).
    byNumberListed.data?.data?.rows?.[0] ??
    byNumberStock.data?.data?.rows?.[0] ??
    byTrading.data?.data?.rows?.[0];
  const partId = slimPart?.id ?? null;

  const fullPart = useQuery({
    queryKey: ["partByEbayItem", "full", partId],
    queryFn: () => get<ISingleCategoryPartResponse>(`/part/${partId}/offers`),
    enabled: Boolean(partId),
    staleTime,
    retry: false,
    refetchOnWindowFocus: false,
  });

  if (DEBUG) {
    dlog("resolved", {
      itemNumber,
      slimPartId: slimPart?.id ?? null,
      fullPartId: fullPart.data?.data?.id ?? null,
    });
  }

  const part: IPart | undefined = fullPart.data?.data ?? slimPart;

  return {
    part,
    isLoading:
      (enabled && byNumberListed.isLoading) ||
      (byNumberStockEnabled && byNumberStock.isLoading) ||
      (byTradingEnabled && byTrading.isLoading) ||
      (byQueryEnabled && byQuery.isLoading) ||
      (Boolean(partId) && fullPart.isLoading),
    isError:
      byNumberListed.isError &&
      (byNumberStock.isError || !byNumberStockEnabled) &&
      (byTrading.isError || !byTradingEnabled) &&
      (byQuery.isError || !byQueryEnabled) &&
      !partId,
  };
}
